import axios from "axios";
import { DataTable } from "simple-datatables";

("use strict");

export class DirectionsControl {
  constructor(directionDiv, map, apiUrls) {
    this.apiUrls = apiUrls;
    axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
    axios.defaults.headers.common["Content-Type"] = "application/json";
    axios.defaults.headers.common["X-CSRF-Token"] = apiUrls["csrfToken"];

    const routingForm = document.querySelector("#routingForm");
    routingForm.addEventListener("submit", (event) => {
      if (!routingForm.checkValidity()) {
        event.stopPropagation();
      }

      event.preventDefault();
      routingForm.classList.add("was-validated");
      this.search();
    });

    // Handle the reset button event
    routingForm.addEventListener("reset", () => {
      this.directionDiv.querySelector("#bookmarkRoute").disabled = true;
      this.directionDiv.querySelector("#directionsList").innerHTML = "";
      routingForm.classList.remove("was-validated");
    });

    this.directionDiv = directionDiv;
    this.map = map;
    this.control = this;
    this.bookmarkStorage = [];

    // Clear starting input on hit X
    document.getElementById("clear-start").addEventListener("click", () => {
      document.getElementById("startingPoint").value = "";
      routingForm.classList.remove("was-validated");
    });

    // Clear destination input on hit X
    document.getElementById("clear-destination").addEventListener("click", () => {
      document.getElementById("destination").value = "";
      routingForm.classList.remove("was-validated");
    });

    // eslint-disable-next-line no-undef
    this.directionsService = new google.maps.DirectionsService();
    const directionsRendererOptions = {
      draggable: true,
      map: this.map,
      panel: this.directionDiv.querySelector("#directionsList"),
      polylineOptions: {
        strokeColor: "#00ffee",
      },
    };
    // eslint-disable-next-line no-undef
    window.directionsRenderer = new google.maps.DirectionsRenderer(directionsRendererOptions);

    window.directionsRenderer.addListener("directions_changed", () => {
      this.triggerRadiusRecalculation();
      this.exportButton();
    });

    const routeSaveButton = document.getElementById("bookmarkRoute");
    routeSaveButton.addEventListener("click", (event) => {
      event.preventDefault();
      this.bookmarkRoute();
    });

    this.directionDiv.querySelector("input[name=limitRadius]").addEventListener("change", () => {
      this.exportButton();
      this.triggerRadiusRecalculation();
    });

    this.directionDiv.querySelectorAll("input[name=radius]").forEach((element) => {
      element.addEventListener("change", () => {
        window.dispatchEvent(new CustomEvent("radiusHasChanged", {}));
        this.directionDiv.querySelector("input[name=limitRadius]").checked = true;
      });
    });

    this.directionDiv.querySelector("button[id=resetRoute]").addEventListener("click", () => {
      this.clearRoute();
    });
    this.exportButton();
    this.init();
  }

  triggerRadiusRecalculation() {
    if (this.directionDiv.querySelector("input[name=limitRadius]").checked) {
      window.dispatchEvent(new CustomEvent("limitRadiusHasChecked", {}));
    } else {
      window.dispatchEvent(new CustomEvent("limitRadiusHasUnchecked", {}));
    }
  }

  init() {
    this.initBookmarks();
  }

  initBookmarks() {
    this.bookmarkTable = new DataTable("#directionBookmarksTable", {
      searchable: true,
      fixedHeight: false,
      columns: [
        {
          select: 4,
          render: this.addRemoveButtonToLastColumn,
        },
      ],
    });

    // simple-datatables re-renders its rows on every update/page/search, so the click handling is
    // delegated once on the table element instead of being re-attached per render.
    this.addEventClickHandlersToRows();

    const self = this;
    axios
      .get(this.apiUrls.getBookmarks)
      .then((response) => {
        this.bookmarkStorage = response.data;

        // Five cells per row: the last, empty one is the column the remove button is rendered
        // into (simple-datatables does not pad rows that are shorter than the header).
        const transformedBookmarks = this.bookmarkStorage.map((bookmark) => {
          return [
            bookmark["name"],
            bookmark["start"]["name"],
            bookmark["end"]["name"],
            String(bookmark["waypointCount"]),
            "",
          ];
        });

        const rows = { data: transformedBookmarks };
        self.bookmarkTable.insert(rows);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  bookmarkRoute() {
    const bookmarkName = window.prompt(
      "Add an optional name for the bookmark (Just click OK if you don't want a name)",
      ""
    );
    const self = this;
    if (bookmarkName === null) {
      return;
    }

    const directions = window.directionsRenderer.getDirections();
    const waypoints = this.getWaypoints(directions);
    waypoints["name"] = bookmarkName;
    axios
      .post(this.apiUrls.saveBookmarks, waypoints)
      .then(() => {
        let newRow = [waypoints.name, waypoints.start.name, waypoints.end.name, String(waypoints.waypointCount), ""];
        self.bookmarkTable.rows.add(newRow);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  /**
   * @param {number} rowIndex data index of the table row (simple-datatables `data-index`)
   */
  deleteBookmark(rowIndex) {
    let selectedBookmark = null;

    if (this.bookmarkStorage[rowIndex] !== null) {
      selectedBookmark = this.bookmarkStorage[rowIndex];
    }

    if (selectedBookmark === null) {
      return;
    }

    const self = this;
    axios
      .post(this.apiUrls.deleteBookmark, selectedBookmark)
      .then(() => {
        self.bookmarkTable.rows.remove(rowIndex);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  /**
   * Get all waypoints of a route
   *
   * @param directions
   * @returns {{start: {lat: *, lng: *, name: *}, end: {lat: *, lng: *, name: *}, distance, waypoints: Array, waypointCount}}
   */
  getWaypoints(directions) {
    const rleg = directions.routes[0].legs[0];
    const waypoints = {
      start: {
        lat: rleg.start_location.lat(),
        lng: rleg.start_location.lng(),
        name: rleg.start_address,
      },
      end: {
        lat: rleg.end_location.lat(),
        lng: rleg.end_location.lng(),
        name: rleg.end_address,
      },
      distance: rleg.distance.value,
      waypoints: [],
      waypointCount: rleg.via_waypoints.length,
    };

    rleg.via_waypoints.forEach((value, index) => {
      waypoints.waypoints[index] = {
        lat: value.lat(),
        lng: value.lng(),
      };
    });

    return waypoints;
  }

  search() {
    const request = {
      origin: document.getElementById("startingPoint").value,
      destination: document.getElementById("destination").value,
      /* eslint-disable no-undef */
      unitSystem: google.maps.UnitSystem.METRIC,
      travelMode: google.maps.DirectionsTravelMode.DRIVING,
      provideRouteAlternatives: true,
    };

    document.getElementById("directionsList").innerHTML = "";
    this.calculateAndDisplayRoute(request);
  }

  /**
   * Plans a route programmatically (used by the AI assistant via window.tapaMap).
   *
   * Origin/destination/waypoints are passed as plain address strings (or anything the Google
   * DirectionsService can geocode); the route form inputs are synced so the UI mirrors what the
   * assistant did.
   *
   * @param {{origin: string, destination: string, waypoints?: string[]}} options
   * @returns {boolean}
   */
  planRoute({ origin, destination, waypoints = [] } = {}) {
    if (!origin || !destination) {
      return false;
    }

    const startInput = document.getElementById("startingPoint");
    const destInput = document.getElementById("destination");
    if (startInput) {
      startInput.value = origin;
    }
    if (destInput) {
      destInput.value = destination;
    }

    const request = {
      origin,
      destination,
      waypoints: (waypoints || []).filter(Boolean).map((location) => ({ location, stopover: false })),
      /* eslint-disable no-undef */
      unitSystem: google.maps.UnitSystem.METRIC,
      travelMode: google.maps.DirectionsTravelMode.DRIVING,
      provideRouteAlternatives: true,
    };

    const list = document.getElementById("directionsList");
    if (list) {
      list.innerHTML = "";
    }
    // Resolves once the route is drawn (or failed), so a following assistant action such as
    // assessRoute runs against the new route and not against the previous one.
    return this.calculateAndDisplayRoute(request);
  }

  /**
   * Clears the currently displayed route and resets the radius filter. Shared by the reset button
   * and the assistant facade.
   *
   * @returns {boolean}
   */
  clearRoute() {
    window.directionsRenderer.set("directions", null);
    this.exportButton();
    window.dispatchEvent(new CustomEvent("limitRadiusHasUnchecked", {}));
    return true;
  }

  /**
   * Promise wrapper around directionsService.route, used by the safety routing flow to compose a
   * baseline request and a waypoint-injected request sequentially without nesting callbacks. Rejects
   * with the Google status string on failure.
   *
   * @param {google.maps.DirectionsRequest} request
   * @returns {Promise<google.maps.DirectionsResult>}
   */
  requestRoutePromise(request) {
    return new Promise((resolve, reject) => {
      this.directionsService.route(request, (response, status) => {
        if (status === "OK") {
          resolve(response);
        } else {
          reject(status);
        }
      });
    });
  }

  /**
   * Requests and renders a route. The Google call is asynchronous; the returned promise resolves
   * with true once the route is displayed and with false on any failure (never rejects), so callers
   * that need the route to exist (assistant action chains) can wait for it.
   *
   * @param {google.maps.DirectionsRequest} request
   * @returns {Promise<boolean>}
   */
  calculateAndDisplayRoute(request) {
    const self = this;
    return new Promise((resolve) => {
      this.directionsService.route(request, (response, status) => {
        const routeSaveButton = self.directionDiv.querySelector("#bookmarkRoute");
        if (status === "OK") {
          window.directionsRenderer.setDirections(response);
          routeSaveButton.disabled = false;
          this.exportButton();
          this.triggerRadiusRecalculation();
          resolve(true);
          return;
        }
        if (status === "NOT_FOUND" || status === "ZERO_RESULTS") {
          // NOT_FOUND == at least one of the locations could not be geocoded
          // ZERO_RESULTS == no route could be found between the origin and destination
          document.getElementById("help-block").innerText = "No route found.";
          document.getElementById("directionsList").innerHTML =
            '<span class="text-danger">Route Planning Error<br>Please enter more specific details in your search request, for example the country (Liverpool, UK).</span>';
          routeSaveButton.disabled = true;
        } else {
          alert("An internal error occurred. Directions request failed due to error: " + status);
          routeSaveButton.disabled = true;
        }
        resolve(false);
      });
    });
  }

  /**
   * simple-datatables column renderer: `cell` is the virtual TD node, the returned HTML string
   * becomes its content.
   */
  addRemoveButtonToLastColumn(data, cell) {
    cell.attributes = cell.attributes || {};
    cell.attributes.class = [cell.attributes.class, "removeRow"].filter(Boolean).join(" ");
    return '<span title="Remove bookmark"><i class="bi-dash-lg remove"></span>';
  }

  exportButton() {
    const exportButton = this.directionDiv.querySelector("#export-markers");
    if (exportButton) {
      if (
        window.directionsRenderer.get("directions") &&
        this.directionDiv.querySelector("input[name=limitRadius]").checked
      ) {
        exportButton.disabled = false;
      } else {
        exportButton.disabled = true;
      }
    }
  }

  addEventClickHandlersToRows() {
    this.bookmarkTable.dom.addEventListener("click", (event) => {
      const cell = event.target.closest("td");
      const row = event.target.closest("tr");
      if (!cell || !row || row.dataset.index === undefined) {
        return;
      }
      const rowIndex = Number(row.dataset.index);
      if (cell.classList.contains("removeRow")) {
        this.deleteBookmark(rowIndex);
        return;
      }
      if (this.bookmarkStorage[rowIndex] !== null) {
        const selectedBookmark = this.bookmarkStorage[rowIndex];
        this.setRoute(selectedBookmark);
        this.directionDiv.querySelector("#startingPoint").value = selectedBookmark.start.name;
        this.directionDiv.querySelector("#destination").value = selectedBookmark.end.name;
      }
    });
  }

  setRoute(data) {
    const waypoints = data.waypoints.map((waypoint) => {
      return {
        location: new google.maps.LatLng(waypoint.lat, waypoint.lng),
        stopover: false,
      };
    });

    const request = {
      origin: new google.maps.LatLng(data.start.lat, data.start.lng),
      destination: new google.maps.LatLng(data.end.lat, data.end.lng),
      waypoints: waypoints,
      travelMode: google.maps.DirectionsTravelMode.DRIVING,
    };
    this.calculateAndDisplayRoute(request);
  }
}
